import { Button } from "components/DesignSystem/Button/Button";
import { Cross } from "components/icons/Cross";
import { DragIcon } from "components/icons/DragIcon";
import classNames from "classnames";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { CrmFilterField } from "types/Models/crmFields";

const DRAG_TYPE = "CRM_EXPORT_SELECTED_FIELD";

type DragItem = { index: number };

type CrmSelectedFieldRowProps = {
  field: CrmFilterField;
  index: number;
  onRemove: (fieldKey: string) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
};

/** Same useDrag/useDrop row-reorder pattern as CustomizeRevenueMetricsPanel's DraggableSection -
 * the one drag-reorder precedent already in this codebase. */
export const CrmSelectedFieldRow = ({
  field,
  index,
  onRemove,
  onMove,
}: CrmSelectedFieldRowProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DRAG_TYPE,
      item: { index },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [index]
  );

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: DRAG_TYPE,
      drop: (draggedItem: DragItem) => {
        if (draggedItem.index !== index) {
          onMove(draggedItem.index, index);
        }
      },
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [index, onMove]
  );

  drag(drop(ref));

  const handleRemove = () => {
    onRemove(field.field_key);
  };

  return (
    <div
      ref={ref}
      className={classNames(
        "t-flex t-cursor-grab t-items-center t-gap-2 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-px-3 t-py-2",
        {
          "!t-cursor-grabbing": isDragging,
          "t-opacity-100": !isDragging,
          "t-opacity-25": isOver,
        }
      )}
    >
      <span className="t-shrink-0 t-select-none t-text-neutral-40">
        <DragIcon />
      </span>
      <span className="t-grow t-truncate t-text-body-sm t-text-text-60">
        {field.label}
      </span>
      <Button
        type="button"
        size="small"
        customType="ghost_icon"
        onClick={handleRemove}
        aria-label={`Remove ${field.label} column`}
      >
        <Cross />
      </Button>
    </div>
  );
};
